import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Button,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { DatePicker } from '@material-ui/pickers';
import { Reorder } from '@material-ui/icons';
import AsyncSearch from 'components/AsyncSearch';
import useDebounce from 'hooks/useDebounce';
import moment from 'moment';
import { USER_OPERATIONS } from 'helper';
import { RootState } from 'interfaces';
import Excel from 'assets/icons/svg/Excel';
import {
  getPartnerRatingAreas,
  getPartnerRatingPartners,
  getPartnerRatingRealEstates,
  getPartnerRatingServiceOrgs,
} from '../../api';
import { IArea, IPartner, IRealEstate, IServiceOrg, IServiceType } from '../../interfaces';
import { Points, Recommendations } from '../../components';
import {
  useExportAllPartnersRatingByPartnerExcelReport,
  useExportPartnersRatingByPartnerExcelReport,
  useGetPartnersRatingByPartner,
  useGetServiceTypes,
  useReorderServiceTypes,
} from './hooks';
import { ReorderServiceTypes, PartnersRatingReport } from './components';

const ByPartners = () => {
  // Global state
  const operations = useSelector((state: RootState) => state.operations);

  // Local state
  const [selectedServiceType, setSelectedServiceType] = useState<IServiceType | null>(null);
  const [selectedServiceOrgs, setServiceOrgs] = useState<IServiceOrg[]>([]);
  const [selectedAreas, setSelectedAreas] = useState<IArea[]>([]);
  const [selectedRealEstates, setSelectedRealEstates] = useState<IRealEstate[]>([]);
  const [selectedPartners, setSelectedPartners] = useState<IPartner[]>([]);
  const [isActivePartner, setIsActivePartner] = useState(1);
  const [selectedYear, setSelectedYear] = useState(() => moment());
  const [serviceTypesOrderDialogOpened, setOpenedServiceTypesOrderDialog] = useState(false);
  const [pageNo, setPageNo] = useState(1);
  const pageSize = 200;

  const debouncedServiceOrgs = useDebounce(selectedServiceOrgs);
  const debouncedAreas = useDebounce(selectedAreas);
  const debouncedRealEstates = useDebounce(selectedRealEstates);
  const debouncedPartners = useDebounce(selectedPartners);
  const debouncedYear = useDebounce(selectedYear);

  const { fetchPartnersRatingByPartner, ...partnersRatingState } = useGetPartnersRatingByPartner();
  const { exportPartnersRatingByPartnerExcelReport, ...partnersRatingByPartnerExcelState } =
    useExportPartnersRatingByPartnerExcelReport();
  const { exportAllPartnersRatingByPartnerExcelReport, ...allPartnersRatingByPartnerExcelState } =
    useExportAllPartnersRatingByPartnerExcelReport();
  const serviceTypesState = useGetServiceTypes((serviceTypes) => setSelectedServiceType(serviceTypes[0]));
  const { changeServiceTypesOrder, ...reorderServiceTypesState } = useReorderServiceTypes(() => {
    serviceTypesState.fetchServiceTypes();
    setOpenedServiceTypesOrderDialog(false);
  });

  const params = {
    pageNo,
    pageSize,
    serviceOrgGuids: debouncedServiceOrgs.map((serviceOrg) => serviceOrg.serviceOrgGuid),
    areaIds: debouncedAreas.map((area) => area.areaId),
    realEstateGuids: debouncedRealEstates.map((realEstate) => realEstate.realEstateGuid),
    partnerGuids: debouncedPartners.map((partner) => partner.partnerGuid),
    serviceTypeId: selectedServiceType?.serviceTypeId,
    year: selectedYear ? moment(debouncedYear).get('year') : null,
    isActivePartner: Boolean(isActivePartner),
  };
  const mapKey = JSON.stringify(
    Object.entries(params)
      .map(([, value]) => value)
      .filter(Boolean),
  );

  function handleResetFilters() {
    setServiceOrgs([]);
    setSelectedAreas([]);
    setSelectedRealEstates([]);
    setSelectedPartners([]);
    setIsActivePartner(1);
    setSelectedYear(moment());
  }

  useEffect(() => {
    if (!selectedServiceType) return;

    fetchPartnersRatingByPartner({ params });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    pageNo,
    debouncedServiceOrgs,
    debouncedAreas,
    debouncedRealEstates,
    debouncedPartners,
    selectedServiceType,
    debouncedYear,
    isActivePartner,
  ]);

  const partnersRating = partnersRatingState.data.get(mapKey) || { list: [], totalCount: 0 };

  if (!selectedServiceType) return null;

  return (
    <Box sx={{ bgcolor: '#fff', borderRadius: 20 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', p: 3, mb: 3, gridGap: 24 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gridGap: 8 }}>
          <Typography component="h2" variant="h5">
            <b>Рейтинг партнеров</b>
          </Typography>

          <Points />
        </Box>

        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3} lg={2}>
            <AsyncSearch
              multiple
              value={selectedPartners}
              fetch={getPartnerRatingPartners}
              remoteSearch
              uniqueBy="partnerGuid"
              matchKeys={['partnerName']}
              getOptionLabel={(option) => option.partnerName}
              limitTags={1}
              disabled={partnersRatingState.loading}
              onChange={(event, options) => setSelectedPartners(options)}
              renderInput={(props) => <TextField {...props} label="Партнеры" variant="filled" size="small" />}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3} lg={2}>
            <AsyncSearch
              multiple
              value={selectedServiceOrgs}
              fetch={getPartnerRatingServiceOrgs}
              uniqueBy="serviceOrgGuid"
              matchKeys={['currentName']}
              getOptionLabel={(option) => option.currentName}
              limitTags={1}
              disabled={partnersRatingState.loading}
              onChange={(event, options) => setServiceOrgs(options)}
              renderInput={(props) => <TextField {...props} label="Дирекции" variant="filled" size="small" />}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3} lg={2}>
            <AsyncSearch
              multiple
              value={selectedAreas}
              fetch={getPartnerRatingAreas}
              config={{
                params: { serviceOrgGuids: selectedServiceOrgs.map((serviceOrg) => serviceOrg.serviceOrgGuid) },
              }}
              uniqueBy="areaId"
              matchKeys={['areaName']}
              getOptionLabel={(option) => option.areaName}
              limitTags={1}
              disabled={partnersRatingState.loading}
              onChange={(event, options) => setSelectedAreas(options)}
              renderInput={(props) => <TextField {...props} label="Участки" variant="filled" size="small" />}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3} lg={2}>
            <AsyncSearch
              multiple
              value={selectedRealEstates}
              fetch={getPartnerRatingRealEstates}
              remoteSearch
              config={{ params: { areaIds: selectedAreas.map((area) => area.areaId) } }}
              uniqueBy="realEstateGuid"
              matchKeys={['realEstateName']}
              getOptionLabel={(option) => option.realEstateName}
              limitTags={1}
              disabled={partnersRatingState.loading}
              onChange={(event, options) => setSelectedRealEstates(options)}
              renderInput={(props) => (
                <TextField {...props} label="Объекты недвижимости" variant="filled" size="small" />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3} lg={2}>
            <FormControl variant="filled" size="small" disabled={partnersRatingState.loading} fullWidth>
              <InputLabel>Договор с партнером</InputLabel>

              <Select
                name="isActivePartner"
                value={isActivePartner}
                label="Тип"
                onChange={(event) => setIsActivePartner(Number(event.target.value))}
              >
                <MenuItem value={1}>Действующие</MenuItem>
                <MenuItem value={0}>Недействующие</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={3} lg={1}>
            <DatePicker
              variant="inline"
              inputVariant="filled"
              size="small"
              label="Год"
              format="yyyy"
              openTo="year"
              views={['year']}
              value={selectedYear}
              disabled={partnersRatingState.loading}
              onChange={(date) => date && setSelectedYear(moment(date))}
              fullWidth
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3} lg={1}>
            <Button variant="outlined" onClick={handleResetFilters}>
              Сбросить
            </Button>

            <Tooltip title="Полная выгрузка рейтинга партнеров">
              <span>
                <IconButton
                  aria-label="Скачать отчет в Excel"
                  onClick={() => exportAllPartnersRatingByPartnerExcelReport({ params })}
                  disabled={
                    !partnersRating.totalCount ||
                    partnersRatingState.loading ||
                    allPartnersRatingByPartnerExcelState.loading
                  }
                >
                  <Excel style={{ color: '#3C763D' }} />
                </IconButton>
              </span>
            </Tooltip>
          </Grid>
        </Grid>

        <Recommendations />

        <Box sx={{ display: 'flex', alignItems: 'center', gridGap: 16 }}>
          {operations.includes(USER_OPERATIONS.ADMIN) && (
            <>
              <Tooltip title="Изменить порядок">
                <IconButton onClick={() => setOpenedServiceTypesOrderDialog(true)}>
                  <Reorder />
                </IconButton>
              </Tooltip>

              <ReorderServiceTypes
                disabled={reorderServiceTypesState.loading}
                serviceTypes={serviceTypesState.data}
                open={serviceTypesOrderDialogOpened}
                onClose={() => setOpenedServiceTypesOrderDialog(false)}
                onSave={changeServiceTypesOrder}
              />
            </>
          )}

          <Tabs
            value={selectedServiceType?.serviceTypeId}
            variant="scrollable"
            scrollButtons="auto"
            textColor="primary"
            indicatorColor="primary"
          >
            {serviceTypesState.data.map((serviceType) => (
              <Tab
                key={serviceType.serviceTypeId}
                label={serviceType.serviceTypeName}
                value={serviceType.serviceTypeId}
                onClick={() => setSelectedServiceType(serviceType)}
              />
            ))}
          </Tabs>
        </Box>
      </Box>

      <PartnersRatingReport
        {...partnersRatingState}
        data={partnersRating}
        isActivePartner={Boolean(isActivePartner)}
        serviceType={selectedServiceType}
        year={selectedYear.get('year')}
        page={pageNo}
        pageSize={pageSize}
        exportExcelLoading={partnersRatingByPartnerExcelState.loading}
        onExportExcel={() => exportPartnersRatingByPartnerExcelReport({ params })}
        onChangePage={(page) => setPageNo(page)}
      />
    </Box>
  );
};

export default ByPartners;
